// TanStack Router wiring. Single SPA with three view states:
//
//   /sign-in       — public; the unauthenticated entry.
//   /privacy, /imprint, /support — public; Loom's legal pages (`legal/`).
//   /projects      — auth-required; loader fetches /auth/me, redirects on 401.
//   /chat/$id      — auth-required; renders ChatPage against the sessionId.
//   /account       — approved-only; who is signed in, connections, sign-out.
//   /focus/meetings?project=<id>, /focus/meetings/import?project=<id>,
//   /focus/meetings/$meetingId — approved-only; meetings to tickets, always
//                    one project's.
//   /m             — public; a meeting's magic link (`#k=<token>`).
//
// The post-OAuth callback hand-off lives on the server: it sets the session
// cookie and 302s back to LOOM_POST_AUTH_PATH (`/` in every env).
//
// Until the first load has a match, `BootGate` (the router's `InnerWrap`)
// shows the boot frame, so a load never paints a blank page. There is no
// pending component: an in-app navigation keeps the page it leaves until the
// next one is ready (`boot/BootScreen.tsx` says why).

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";

import { fetchMe, invalidateMe } from "./auth/useAuth";
import { BootGate } from "./boot/BootScreen";
import { SignInPage } from "./auth/SignInPage";
import { LegalPage, legalSearch } from "./legal/LegalPage";
import { PendingApprovalPage } from "./access/PendingApprovalPage";
import { AccountPage } from "./account/AccountPage";
import { accountBackTarget } from "./account/accountLinks";
import { AccessPage } from "./admin/AccessPage";
import { AdminHomePage } from "./admin/AdminHomePage";
import { AnthropicKeysAdminPage } from "./admin/AnthropicKeysAdminPage";
import { CodeMapAdminPage } from "./admin/CodeMapAdminPage";
import { PickerPage } from "./projects/PickerPage";
import { ProjectChatPage } from "./projects/ProjectChatPage";
import { ChatPage } from "./chat/ChatPage";
import { DashboardPage } from "./dashboard/DashboardPage";
import { IssuePage } from "./dashboard/IssuePage";
import { FocusView } from "./focus/FocusView";
import { MeetingImportPage } from "./focus/meetings/MeetingImportPage";
import { MeetingLinkPage } from "./focus/meetings/MeetingLinkPage";
import { MeetingPage } from "./focus/meetings/MeetingPage";
import { MeetingsPage } from "./focus/meetings/MeetingsPage";
import { meetingsHref, meetingsProjectOf } from "./focus/meetings/MeetingsEntry";
import { AnalyticsPage } from "./analytics/AnalyticsPage";
import { TranscriptPage } from "./analytics/TranscriptPage";
import { RouteError, RouteNotFound } from "./lib/RouteError";
import { useHereLocation } from "./focus/figma/useHereLocation";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Shared loader guard for the customer-facing protected routes. Three gates,
// in order: not signed in → /sign-in; signed in but a not-approved NON-admin
// (pending or blocked) → /pending. Admins always pass (they need to reach
// /admin/access to approve people, and shouldn't lock themselves out).
//
// DO NOT use this on /sign-in, /pending, or the OAuth callback — a pending user
// must keep reaching those, else the redirect deadlocks. The /admin/access
// route deliberately uses a sign-in-only loader (like analyticsRoute): the
// admin gate is server-owned (403 → forbidden state), not a client role check.
const requireApproved = async (): Promise<void> => {
  const me = await fetchMe();
  if (me === null) throw redirect({ to: "/sign-in" });
  if (!me.isAdmin && me.approvalStatus !== "approved") {
    throw redirect({ to: "/pending" });
  }
};

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: () => <SignInPage />,
});

// Loom's legal pages. PUBLIC: no guard, and the page asks the server nothing,
// so a signed-out visitor, a pending user and an app reviewer all reach them
// (nginx's SPA fallback serves the shell for any path it does not proxy).
// `?lang=en|de` pins the language; anything else is dropped.
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  validateSearch: legalSearch,
  component: function PrivacyRoute() {
    const { lang } = privacyRoute.useSearch();
    return <LegalPage kind="privacy" {...(lang ? { lang } : {})} />;
  },
});

const imprintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/imprint",
  validateSearch: legalSearch,
  component: function ImprintRoute() {
    const { lang } = imprintRoute.useSearch();
    return <LegalPage kind="imprint" {...(lang ? { lang } : {})} />;
  },
});

const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/support",
  validateSearch: legalSearch,
  component: function SupportRoute() {
    const { lang } = supportRoute.useSearch();
    return <LegalPage kind="support" {...(lang ? { lang } : {})} />;
  },
});

// A meeting's magic link. PUBLIC, like the legal
// pages: the holder may have no Loom account, so no guard and no `/auth/me`.
// The page reads the token from `#k=` once and takes the fragment out of the
// address bar through the router (a bare `replaceState` would move the URL
// behind the router's back; `focus/figma/useHereLocation.ts`).
const meetingLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/m",
  component: function MeetingLinkRoute() {
    const router = useRouter();
    return (
      <MeetingLinkPage
        clearHash={() => router.history.replace("/m", router.history.location.state)}
      />
    );
  },
});

// Focus is the default customer surface; the dashboard is the dev/debug view
// at /projects. `/` asks nothing itself: `/focus`'s own guard sends a
// signed-out user to /sign-in and a pending one to /pending. Asking here as
// well cost every load of `/` — and every sign-in, which lands on `/` — a
// second `/auth/me`.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/focus" });
  },
  component: () => null,
});

// Focus — the step-based, one-question-at-a-time customer surface. The chosen
// project and the refine target are URL-reflected as sibling routes so a reload
// resumes where the user was (the wizard steps stay internal):
//   /focus                                      → project picker
//   /focus/projects/$projectId                  → per-project wizard
//   /focus/projects/$projectId/issues/$iid      → refine chat for that issue
// `FocusView` (`focus/FocusView.tsx`) wires the shared navigation callbacks
// once; each route supplies its params. The Focus panel (`panel/`) builds the
// same three routes on its own router from the same `FocusView`.
const focusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/focus",
  beforeLoad: requireApproved,
  component: function FocusRoute() {
    return <FocusView projectId={null} refineIid={null} />;
  },
});

// Meetings to tickets. Sibling routes of `/focus`,
// approved users only. With `LOOM_MEETINGS_ENABLE` off the pages render
// "not found" off `GET /api/meetings/status` (`focus/meetings/`).
//
// A meeting is always a project's: `/focus/meetings?project=<id>`, read from
// the raw URL (no `validateSearch`, as `/account`), reached from the
// project's Focus selection screen, whose screen Back returns to. Without a
// project the page says to pick one first. Keyed by the project, so another
// project's list never shows a moment of this one's rows.
const focusMeetingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/focus/meetings",
  beforeLoad: requireApproved,
  component: function FocusMeetingsRoute() {
    const navigate = useNavigate();
    const { here } = useHereLocation();
    const projectId = meetingsProjectOf(here.search);
    return (
      <MeetingsPage
        key={projectId ?? "none"}
        projectId={projectId}
        onBack={() =>
          projectId === null
            ? navigate({ to: "/focus" })
            : navigate({
                to: "/focus/projects/$projectId",
                params: { projectId: String(projectId) },
              })
        }
        onOpenMeeting={(meetingId) =>
          navigate({ to: "/focus/meetings/$meetingId", params: { meetingId } })
        }
        onOpenImport={(href) => navigate({ href })}
      />
    );
  },
});

// A meeting recorded without Loom, processed for the project the URL names.
// A STATIC path, so the router ranks it above
// `/focus/meetings/$meetingId` (`test/meetings-import-page.test.tsx` pins
// that). Back returns to the project's meetings page.
const focusMeetingImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/focus/meetings/import",
  beforeLoad: requireApproved,
  component: function FocusMeetingImportRoute() {
    const navigate = useNavigate();
    const { here } = useHereLocation();
    const projectId = meetingsProjectOf(here.search);
    return (
      <MeetingImportPage
        key={projectId ?? "none"}
        projectId={projectId}
        onBack={() =>
          projectId === null ? navigate({ to: "/focus" }) : navigate({ href: meetingsHref(projectId) })
        }
        onOpenMeeting={(meetingId, opts) =>
          navigate({
            to: "/focus/meetings/$meetingId",
            params: { meetingId },
            replace: opts?.replace === true,
          })
        }
      />
    );
  },
});

/** Back from one meeting: its project's meetings, or the projects without one. */
const meetingsListHref = (projectId: number | null): string =>
  projectId === null ? "/focus" : meetingsHref(projectId);

const focusMeetingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/focus/meetings/$meetingId",
  beforeLoad: requireApproved,
  component: function FocusMeetingRoute() {
    const navigate = useNavigate();
    const { meetingId } = focusMeetingRoute.useParams();
    return (
      <MeetingPage
        meetingId={meetingId}
        onBack={(projectId) => navigate({ href: meetingsListHref(projectId) })}
        onDeleted={(projectId) =>
          navigate({ href: meetingsListHref(projectId), replace: true })
        }
      />
    );
  },
});

// The account page. Back returns to the page that linked here, carried as
// `?from=` (see `account/accountLinks.ts`) and read from the raw URL: no
// `validateSearch`, so the OAuth markers the connection rows strip and the
// `from` they keep round-trip untouched.
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  beforeLoad: requireApproved,
  component: function AccountRoute() {
    const navigate = useNavigate();
    const { here } = useHereLocation();
    const back = accountBackTarget(
      new URLSearchParams(here.search).get("from"),
    );
    return (
      <AccountPage
        onBack={() => navigate({ href: back })}
        onOpenProjects={() => navigate({ to: "/focus" })}
        onSignedOut={() => window.location.assign("/sign-in")}
      />
    );
  },
});

const focusProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/focus/projects/$projectId",
  beforeLoad: requireApproved,
  component: function FocusProjectRoute() {
    const { projectId } = focusProjectRoute.useParams();
    return (
      <FocusView projectId={Number.parseInt(projectId, 10)} refineIid={null} />
    );
  },
});

const focusRefineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/focus/projects/$projectId/issues/$iid",
  beforeLoad: requireApproved,
  component: function FocusRefineRoute() {
    const { projectId, iid } = focusRefineRoute.useParams();
    return (
      <FocusView
        projectId={Number.parseInt(projectId, 10)}
        refineIid={Number.parseInt(iid, 10)}
      />
    );
  },
});

// Maintainer-gated analytics dashboard (analytics+postgres Phase 6). The
// loader only enforces sign-in — there is NO maintainer probe here: the viewer
// gate lives server-side (Phase 5), and the page renders its access-denied state
// off the server's 403 (no role is carried in the cookie).
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics",
  beforeLoad: async () => {
    const me = await fetchMe();
    if (me === null) throw redirect({ to: "/sign-in" });
  },
  component: function AnalyticsRoute() {
    const navigate = useNavigate();
    // Reached from the /admin hub — return there, not straight to /focus.
    return <AnalyticsPage onBack={() => navigate({ to: "/admin" })} />;
  },
});

// One session's chat transcript — a SIBLING of /analytics, not a child, so the
// URL survives a reload and can be pasted into a ticket. Same sign-in-only
// loader for the same reason: the Tier-3 grant is server-owned and the page
// renders the 403 as a state.
const transcriptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/sessions/$sessionId",
  beforeLoad: async () => {
    const me = await fetchMe();
    if (me === null) throw redirect({ to: "/sign-in" });
  },
  component: function TranscriptRoute() {
    const navigate = useNavigate();
    const { sessionId } = transcriptRoute.useParams();
    return (
      <TranscriptPage
        sessionId={sessionId}
        onBack={() => navigate({ to: "/analytics" })}
      />
    );
  },
});

// The not-yet-approved holding page. Sign-in-only loader — it must stay
// reachable by a pending user (requireApproved redirects HERE). An already
// -approved user who lands here is bounced on to /focus so it can't be a dead
// end after approval.
const pendingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pending",
  beforeLoad: async () => {
    const me = await fetchMe();
    if (me === null) throw redirect({ to: "/sign-in" });
    if (me.isAdmin || me.approvalStatus === "approved") {
      throw redirect({ to: "/focus" });
    }
  },
  component: function PendingRoute() {
    const navigate = useNavigate();
    // Re-check: a fresh navigate re-runs beforeLoad → fetchMe, so an account
    // approved since landing here is forwarded on to /focus. The cached answer
    // is dropped first, or `fetchMe` would hand back the "pending" it read a
    // moment ago and bounce the user straight back here.
    return (
      <PendingApprovalPage
        onRecheck={() => {
          invalidateMe();
          void navigate({ to: "/focus" });
        }}
      />
    );
  },
});

// Sign-in-only loader shared by every /admin* route — the admin gate is
// server-owned (each page renders its forbidden state off the API's 403). No
// client-side `me.isAdmin` route gate.
const requireSignedIn = async (): Promise<void> => {
  const me = await fetchMe();
  if (me === null) throw redirect({ to: "/sign-in" });
};

// The /admin hub — four nav cards into the actual admin surfaces. Its own
// onBack returns to /focus (it's the top of the /admin tree); the surfaces it
// links to return to /admin, not /focus, when reached from here.
const adminHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: requireSignedIn,
  component: function AdminHomeRoute() {
    const navigate = useNavigate();
    return (
      <AdminHomePage
        onBack={() => navigate({ to: "/focus" })}
        onOpenAccess={() => navigate({ to: "/admin/access" })}
        onOpenCodeMap={() => navigate({ to: "/admin/code-map" })}
        onOpenAnalytics={() => navigate({ to: "/analytics" })}
        onOpenKeys={() => navigate({ to: "/admin/keys" })}
      />
    );
  },
});

// Admin-gated access control. Like analyticsRoute, the loader only enforces
// sign-in — the admin gate is server-owned (the page renders its forbidden
// state off the API's 403). No client-side `me.isAdmin` route gate.
const adminAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/access",
  beforeLoad: requireSignedIn,
  component: function AdminAccessRoute() {
    const navigate = useNavigate();
    // Reached from the /admin hub — return there, not straight to /focus.
    return <AccessPage onBack={() => navigate({ to: "/admin" })} />;
  },
});

// Admin-gated Projekt-Karte provisioning. Same sign-in-only loader + server-
// owned forbidden state as adminAccessRoute.
const adminCodeMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/code-map",
  beforeLoad: requireSignedIn,
  component: function AdminCodeMapRoute() {
    const navigate = useNavigate();
    return <CodeMapAdminPage onBack={() => navigate({ to: "/admin" })} />;
  },
});

// Admin-gated Anthropic key assignment. Same sign-in-only loader + server-
// owned forbidden state as adminAccessRoute.
const adminKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/keys",
  beforeLoad: requireSignedIn,
  component: function AdminKeysRoute() {
    const navigate = useNavigate();
    return <AnthropicKeysAdminPage onBack={() => navigate({ to: "/admin" })} />;
  },
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  beforeLoad: requireApproved,
  component: function ProjectsRoute() {
    const navigate = useNavigate();
    return (
      <PickerPage
        onProjectPicked={(projectId) =>
          navigate({
            to: "/projects/$projectId",
            params: { projectId: String(projectId) },
          })
        }
      />
    );
  },
});

// Phase 1 — project dashboard landing. The picker hands off here; chat is
// reachable from the dashboard's header (Ask Loom) and via the existing
// `/projects/$projectId/chat[/$sessionId]` routes for deep-links.
const projectDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId",
  beforeLoad: requireApproved,
  component: function ProjectDashboardRoute() {
    const navigate = useNavigate();
    const { projectId } = projectDashboardRoute.useParams();
    const numericProjectId = Number.parseInt(projectId, 10);
    return (
      <DashboardPage
        projectId={numericProjectId}
        onBackToPicker={() => navigate({ to: "/projects" })}
        onNavigateToIssue={(iid) =>
          navigate({
            to: "/projects/$projectId/issues/$iid",
            params: { projectId, iid: String(iid) },
          })
        }
        onNewRequest={() =>
          navigate({
            to: "/projects/$projectId/issues/new",
            params: { projectId },
          })
        }
      />
    );
  },
});

// New-request surface. Same page as the detail route but with `iid=null`,
// so create + refine live on one route. The static `new` segment is
// matched ahead of the `$iid` param by the router.
const projectIssueNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/issues/new",
  beforeLoad: requireApproved,
  component: function ProjectIssueNewRoute() {
    const navigate = useNavigate();
    const { projectId } = projectIssueNewRoute.useParams();
    const numericProjectId = Number.parseInt(projectId, 10);
    return (
      <IssuePage
        projectId={numericProjectId}
        iid={null}
        onBackToDashboard={() =>
          navigate({ to: "/projects/$projectId", params: { projectId } })
        }
        onNavigateToIssue={(nextIid) =>
          navigate({
            to: "/projects/$projectId/issues/$iid",
            params: { projectId, iid: String(nextIid) },
          })
        }
        onCreated={(newIid) =>
          // Replace so Back doesn't return to the now-stale create form.
          navigate({
            to: "/projects/$projectId/issues/$iid",
            params: { projectId, iid: String(newIid) },
            replace: true,
          })
        }
        onBackToPicker={() => navigate({ to: "/projects" })}
      />
    );
  },
});

// Per-issue detail page. Drops the inline modal-only editing into a full
// route so the work item's net (links, MRs, two timelines) has room to
// breathe; the modal-editor still pops over for actual edits.
const projectIssueDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/issues/$iid",
  beforeLoad: requireApproved,
  component: function ProjectIssueDetailRoute() {
    const navigate = useNavigate();
    const { projectId, iid } = projectIssueDetailRoute.useParams();
    const numericProjectId = Number.parseInt(projectId, 10);
    const numericIid = Number.parseInt(iid, 10);
    return (
      <IssuePage
        projectId={numericProjectId}
        iid={numericIid}
        onBackToDashboard={() =>
          navigate({ to: "/projects/$projectId", params: { projectId } })
        }
        onNavigateToIssue={(nextIid) =>
          navigate({
            to: "/projects/$projectId/issues/$iid",
            params: { projectId, iid: String(nextIid) },
          })
        }
        onCreated={(newIid) =>
          navigate({
            to: "/projects/$projectId/issues/$iid",
            params: { projectId, iid: String(newIid) },
            replace: true,
          })
        }
        onBackToPicker={() => navigate({ to: "/projects" })}
      />
    );
  },
});

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat/$sessionId",
  beforeLoad: requireApproved,
  component: function ChatRoute() {
    const navigate = useNavigate();
    const { sessionId } = chatRoute.useParams();
    return (
      <ChatPage
        sessionId={sessionId}
        onPickSession={(id) => navigate({ to: "/chat/$sessionId", params: { sessionId: id } })}
        onBackToPicker={() => navigate({ to: "/projects" })}
      />
    );
  },
});

// Phase 9 — project-scoped chat. The sidebar is per-project; the
// sessionId is optional so users can land on a project before picking a
// session.
const projectChatLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/chat",
  beforeLoad: requireApproved,
  component: function ProjectChatLandingRoute() {
    const navigate = useNavigate();
    const { projectId } = projectChatLandingRoute.useParams();
    const numericProjectId = Number.parseInt(projectId, 10);
    return (
      <ProjectChatPage
        projectId={numericProjectId}
        onSelectSession={(sessionId) =>
          navigate({
            to: "/projects/$projectId/chat/$sessionId",
            params: { projectId, sessionId },
          })
        }
        onBackToPicker={() => navigate({ to: "/projects" })}
      />
    );
  },
});

const projectChatSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$projectId/chat/$sessionId",
  beforeLoad: requireApproved,
  component: function ProjectChatSessionRoute() {
    const navigate = useNavigate();
    const { projectId, sessionId } = projectChatSessionRoute.useParams();
    const numericProjectId = Number.parseInt(projectId, 10);
    return (
      <ProjectChatPage
        projectId={numericProjectId}
        sessionId={sessionId}
        onSelectSession={(nextId) =>
          navigate({
            to: "/projects/$projectId/chat/$sessionId",
            params: { projectId, sessionId: nextId },
          })
        }
        onBackToPicker={() => navigate({ to: "/projects" })}
      />
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  focusRoute,
  focusProjectRoute,
  focusRefineRoute,
  focusMeetingsRoute,
  focusMeetingImportRoute,
  focusMeetingRoute,
  accountRoute,
  analyticsRoute,
  transcriptRoute,
  pendingRoute,
  adminHomeRoute,
  adminAccessRoute,
  adminCodeMapRoute,
  adminKeysRoute,
  signInRoute,
  privacyRoute,
  imprintRoute,
  supportRoute,
  meetingLinkRoute,
  projectsRoute,
  projectDashboardRoute,
  projectIssueNewRoute,
  projectIssueDetailRoute,
  chatRoute,
  projectChatLandingRoute,
  projectChatSessionRoute,
]);

export const router = createRouter({
  routeTree,
  // The boot frame while no route has matched yet: the first load, its
  // redirect chain and its guard's wait. No `defaultPendingComponent`: with
  // one, the router hands a slow match over early, a second frame replaces
  // this one mid-load, and in-app navigations on every surface swap their page
  // for it (`boot/BootScreen.tsx`).
  InnerWrap: BootGate,
  // Every route inherits both. A loader that throws now lands on a screen with
  // a retry and a way home, instead of the router's own "Something went wrong!
  // / Show Error" over the upstream's raw body — which one transient 429 on
  // `/auth/me` was enough to reach, with no control on the page but "Hide
  // Error" and no recovery short of a browser reload.
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: RouteNotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
